<template>
  <div
    class="p-4 sm:p-6 rounded-lg text-lg sm:text-xl text-left relative"
    :class="[
      open
        ? 'bg-gray-300 text-gray-700'
        : status === 'completed' || status === 'cancelled'
        ? 'cursor-not-allowed bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-700'
        : status === 'requested' || status === 'approved'
        ? 'cursor-pointer bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white'
        : 'cursor-pointer bg-gray-200 hover:bg-gray-100 text-gray-500 hover:text-400'
    ]"
    @click.stop="open ? '' : toggleTransfer"
    :style="
      open ? 'background: linear-gradient(150deg, #cbd5e0, #e2e8f0);' : ''
    "
  >
    <div
      class="w-full flex items-center font-normal"
      :class="open ? 'cursor-pointer' : ''"
      @click.stop="toggleTransfer"
    >
      <div class="w-12 h-12">
        <!-- Initial requested state -->
        <unicon
          v-if="status === 'requested'"
          name="comment-question"
          :class="open ? 'text-blue-600' : 'text-blue-500'"
        />
        <!-- Approved state -->
        <unicon
          v-if="status === 'approved'"
          name="truck"
          :class="open ? 'text-yellow-600' : 'text-yellow-400'"
        />
        <!-- Completed state -->
        <unicon
          v-if="status === 'completed'"
          name="check"
          :class="open ? 'text-teal-600' : 'text-teal-400'"
        />
        <!-- Cancelled state -->
        <unicon
          v-if="status === 'cancelled'"
          name="times"
          :class="open ? 'text-red-600' : 'text-red-400'"
        />
      </div>

      <div class="ml-3 w-full flex justify-between items-center">
        <div class="flex flex-col w-full">
          <div class="text-xl sm:text-2xl leading-none font-bold">
            {{ transferHeader }}
          </div>
          <div
            class="text-lg mt-2"
            :class="open ? 'text-gray-600' : 'text-current'"
          >
            {{ transfer.itemName }}
          </div>
        </div>
        <div
          class="mr-3 text-2xl sm:text-3xl"
          :class="open ? 'text-gray-600' : 'text-current'"
        >
          {{ formattedDate }}
        </div>
      </div>
    </div>

    <transition-expand @afterEnter="afterEnter">
      <div v-if="open" class="mt-4 flex flex-wrap relative h-auto">
        <!-- Transfer details section -->
        <div class="w-full">
          <div class="mt-4 p-4 bg-gray-200 rounded-lg">
            <div class="flex justify-between items-center">
              <div class="text-lg font-semibold">
                {{ transfer.itemName }}
              </div>
              <div class="text-lg">
                {{ transfer.quantity }} {{ transfer.unitType }}
              </div>
            </div>
            <div v-if="transfer.notes" class="mt-2 text-gray-600">
              {{ transfer.notes }}
            </div>
            <div v-if="!hasNoSourceStore" class="mt-2 text-sm text-gray-600">
              <div>
                <span class="font-semibold">Unit Cost:</span>
                ${{ formatPrice(transfer.unitCost) }}
              </div>
              <div>
                <span class="font-semibold">Total Cost:</span>
                ${{ formatPrice(transfer.totalCost) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="mt-6 flex w-full justify-between">
          <div class="space-x-4">
            <s-button
              v-if="canCancel"
              class="shadow-lg bg-red-500 hover:bg-red-600"
              @click="handleCancel"
            >
              Cancel
            </s-button>
            <s-button v-if="canEdit" class="shadow-lg" @click="handleEdit">
              Edit
            </s-button>
            <s-button
              v-if="canDelete"
              class="shadow-lg bg-red-600 hover:bg-red-700"
              @click="handleDelete"
            >
              Delete
            </s-button>
          </div>

          <div class="space-x-4">
            <s-button
              v-if="canApprove"
              class="shadow-lg bg-blue-500 hover:bg-blue-600"
              @click="handleApprove"
            >
              Approve
            </s-button>
            <s-button
              v-if="canComplete"
              class="shadow-lg bg-green-500 hover:bg-green-600"
              @click="handleComplete"
            >
              Complete
            </s-button>
          </div>
        </div>
      </div>
    </transition-expand>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TransitionExpand from '@/components/TransitionExpand'
import SButton from '@/components/Button'

export default {
  name: 'Transfer',

  components: {
    TransitionExpand,
    SButton
  },

  props: {
    transferId: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    activeItem: {
      type: Number,
      required: true
    },
    pending: {
      type: Boolean,
      required: false,
      default: false
    },
    closed: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      initialLoad: true,
      open: false,
      activeChild: null
    }
  },

  computed: {
    ...mapGetters('user', ['isManager', 'isAdmin', 'isDeveloper', 'storeName']),
    ...mapGetters('storeModule', ['id']),
    ...mapGetters('storeModule/transfers', ['transferById']),
    ...mapGetters('global', [
      'products',
      'productById',
      'productName',
      'isProduct',
      'stores',
      'settingsStoreIds'
    ]),
    ...mapGetters('recipes', ['recipeById', 'recipeName', 'isRecipe']),

    transfer() {
      return this.transferById(this.transferId)
    },
    status() {
      return this.transfer.status
    },

    isActive() {
      return this.activeItem === this.index
    },

    fromStoreName() {
      return (
        (this.transfer.sourceStoreId &&
          this.settingsStoreIds[this.transfer.sourceStoreId] &&
          this.settingsStoreIds[this.transfer.sourceStoreId].name) ||
        'Unknown Store'
      )
    },

    toStoreName() {
      return (
        (this.transfer.destinationStoreId &&
          this.settingsStoreIds[this.transfer.destinationStoreId] &&
          this.settingsStoreIds[this.transfer.destinationStoreId].name) ||
        'Unknown Store'
      )
    },

    formattedDate() {
      if (!this.transfer.date || !this.transfer.date.seconds) return ''
      const date = new Date(this.transfer.date.seconds * 1000)
      return `${date.getMonth() + 1}/${date.getDate()}`
    },

    formattedCreatedDate() {
      if (!this.transfer.createdAt || !this.transfer.createdAt.seconds)
        return ''
      const date = new Date(this.transfer.createdAt.seconds * 1000)
      return date.toLocaleDateString()
    },

    formattedUpdatedDate() {
      if (!this.transfer.updatedAt || !this.transfer.updatedAt.seconds)
        return ''
      const date = new Date(this.transfer.updatedAt.seconds * 1000)
      return date.toLocaleDateString()
    },

    approved() {
      return this.status === 'approved'
    },

    hasNoSourceStore() {
      return !this.transfer.sourceStoreId
    },

    canApprove() {
      if (this.status !== 'requested') return false
      if (this.transfer.destinationStoreId === this.id) return false
      if (this.hasNoSourceStore) return true
      return this.transfer.sourceStoreId === this.id
    },

    canComplete() {
      if (this.status !== 'approved') return false
      if (this.transfer.destinationStoreId === this.id) return true
      if (this.isAdmin) return true
      return false
    },

    canCancel() {
      if (this.status === 'completed') return false
      if (this.isAdmin) return true
      if (this.status === 'approved') return false
      if (this.transfer.destinationStoreId === this.id && this.isManager)
        return true
      return false
    },

    canEdit() {
      if (this.isAdmin) return true
      if (this.status !== 'requested') return false
      if (this.transfer.destinationStoreId === this.id) return true
      return false
    },

    canDelete() {
      if (this.isDeveloper) return true
      if (this.status !== 'completed') return false
      if (this.isAdmin) return true
      return false
    },

    canManageTransfer() {
      return (
        (this.transfer.sourceStoreId === this.id &&
          this.status === 'requested') ||
        (this.transfer.destinationStoreId === this.id &&
          this.status === 'approved')
      )
    },

    transferHeader() {
      return this.transfer.sourceStoreName
        ? `${this.transfer.sourceStoreName} → ${this.transfer.destinationStoreName}`
        : `To: ${this.transfer.destinationStoreName}`
    }
  },

  watch: {
    activeItem: {
      handler(newValue) {
        console.log('active item change!', this.index, newValue)
      }
    },

    index: {
      handler(newValue) {
        console.log('index change!', this.index, newValue)
      }
    },

    isActive: {
      handler(newValue) {
        if (newValue === false) {
          this.open = false
          this.activeChild = null
        }
      }
    }
  },

  methods: {
    ...mapActions('storeModule/transfers', [
      'updateTransfer',
      'approveTransfer',
      'deleteTransfer',
      'completeTransfer',
      'cancelTransfer'
    ]),

    formatPrice(price) {
      return Number(price || 0).toFixed(2)
    },

    handleComplete() {
      this.completeTransfer({ id: this.transfer.id })
        .then(() => {
          this.toggleTransfer()
        })
        .catch((error) => {
          console.error('Error completing transfer:', error)
        })
    },

    handleCancel() {
      this.cancelTransfer({ id: this.transfer.id })
        .then(() => {
          this.toggleTransfer()
        })
        .catch((error) => {
          console.error('Error cancelling transfer:', error)
        })
    },

    toggleTransfer() {
      if (this.closed && !this.isManager) return
      this.activeChild = null
      this.$emit('focused', this.index)
      this.open = !this.open
    },

    afterEnter(event) {
      console.log('afterEnter: ', event)
    },

    handleEdit() {
      this.$modal.show('modal--product-transfer', {
        transferId: this.transfer.id
      })
    },

    handleApprove() {
      this.approveTransfer({ id: this.transfer.id })
      this.toggleTransfer()
    },

    handleDelete() {
      this.$modal.show('modal--confirm', {
        title: 'Delete Transfer',
        text: 'Are you sure you want to delete this transfer? This action cannot be undone.',
        buttons: [
          {
            title: 'Cancel',
            class: 'bg-gray-500 hover:bg-gray-600',
            handler: () => {
              this.$modal.hide('modal--confirm')
            }
          },
          {
            title: 'Delete',
            class: 'bg-red-600 hover:bg-red-700',
            handler: () => {
              this.deleteTransfer({ id: this.transfer.id })
                .then(() => {
                  this.$modal.hide('modal--confirm')
                  this.toggleTransfer()
                })
                .catch((error) => {
                  console.error('Error deleting transfer:', error)
                })
            }
          }
        ]
      })
    }
  }
}
</script>
